/**
 * medimo Web のページと、電子カルテ連携の Chrome 拡張機能が medimo Web の画面に置く
 * content script の間で `window.postMessage` に載せるメッセージ。拡張機能側の
 * `ehr-extension/src/web/page-protocol.ts` に同じ定義があり、文字列を変えるときは両方を変える。
 *
 * 版の合わないメッセージは双方とも捨てる。返事が無ければ拡張機能が無いものとして
 * 録音を始めるため、片方だけが新しくなっても medimo Web の録音は止まらない。
 */
export const EHR_EXTENSION_CHANNEL = "medimo-ehr-extension";
export const EHR_EXTENSION_PROTOCOL_VERSION = 1;

export type ToExtension =
  | { type: "hello" }
  | { type: "acquire"; requestId: string }
  | { type: "heartbeat"; requestId: string; leaseId: string }
  | { type: "release"; leaseId: string }
  | { type: "handshake-reply"; requestId: string; busy: boolean };

export type FromExtension =
  | { type: "present"; instance: string }
  | { type: "absent"; instance: string }
  | { type: "acquire-granted"; requestId: string; leaseId: string }
  | { type: "acquire-refused"; requestId: string }
  | { type: "acquire-unavailable"; requestId: string }
  | {
      type: "heartbeat-reply";
      requestId: string;
      result: "held" | "lost" | "unavailable";
    }
  | { type: "handshake"; requestId: string };

export const toExtension = (message: ToExtension): Record<string, unknown> => ({
  channel: EHR_EXTENSION_CHANNEL,
  version: EHR_EXTENSION_PROTOCOL_VERSION,
  direction: "to-extension",
  ...message,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const stringOf = (value: Record<string, unknown>, key: string) => {
  const found = value[key];
  return typeof found === "string" && found.length > 0 ? found : null;
};

const HEARTBEAT_RESULTS: ReadonlyArray<"held" | "lost" | "unavailable"> = [
  "held",
  "lost",
  "unavailable",
];

const parseAcquireReply = (
  data: Record<string, unknown>,
  requestId: string,
): FromExtension => {
  const leaseId = stringOf(data, "leaseId");
  if (data.result === "granted" && leaseId !== null) {
    return { type: "acquire-granted", requestId, leaseId };
  }
  return data.result === "refused"
    ? { type: "acquire-refused", requestId }
    : { type: "acquire-unavailable", requestId };
};

const parseHeartbeatReply = (
  data: Record<string, unknown>,
  requestId: string,
): FromExtension => ({
  type: "heartbeat-reply",
  requestId,
  result:
    HEARTBEAT_RESULTS.find((candidate) => candidate === data.result) ??
    "unavailable",
});

const isToPage = (data: unknown): data is Record<string, unknown> =>
  isRecord(data) &&
  data.channel === EHR_EXTENSION_CHANNEL &&
  data.version === EHR_EXTENSION_PROTOCOL_VERSION &&
  data.direction === "to-page";

/**
 * 拡張機能から届いたメッセージを読む。このプロトコルのメッセージでなければ null。取得の
 * 拒否は medimo Web の録音を止めるため、形がすべてそろったときだけ拒否として読み、
 * 読めない返事は使えないものとして扱う
 */
export const parseFromExtension = (data: unknown): FromExtension | null => {
  if (!isToPage(data)) return null;
  const requestId = stringOf(data, "requestId");
  const instance = stringOf(data, "instance");
  switch (data.type) {
    case "present":
    case "absent":
      return instance === null ? null : { type: data.type, instance };
    case "handshake":
      return requestId === null ? null : { type: "handshake", requestId };
    case "acquire-reply":
      return requestId === null ? null : parseAcquireReply(data, requestId);
    case "heartbeat-reply":
      return requestId === null ? null : parseHeartbeatReply(data, requestId);
    default:
      return null;
  }
};
